import { useEffect, useState, useMemo, useCallback } from 'react';
import Select from 'react-select';
import FeatherIcon from 'feather-icons-react';
import { Form, Row, Col, FormGroup, Label, Button, Input, Collapse } from 'reactstrap';

const PropertySelector = ({
    propertyList,
    selectedFilters,
    onPropertySelectionChange,
    onExportExcel,
    frequencyOptions = [],
    yearOptions = [],
}) => {
    const [isFiltersOpen, setIsFiltersOpen] = useState(false);

    const currentProperty = useMemo(
        () => propertyList.find((p) => p._id === selectedFilters.property_id) ?? null,
        [propertyList, selectedFilters.property_id]
    );

    const buildings = useMemo(() => {
        if (!currentProperty?.building) return [];
        return currentProperty.building.filter((b) => b?.buildingName);
    }, [currentProperty]);

    const hasBuildings = buildings.length > 0;
    const hasMultipleBuildings = buildings.length > 1;

    const selectedBuilding = useMemo(
        () => buildings.find((b) => b.buildingName === selectedFilters.building_name) ?? null,
        [buildings, selectedFilters.building_name]
    );

    const derivedPropertyType = useMemo(() => {
        if (selectedBuilding?.buildingType) return selectedBuilding.buildingType;
        if (selectedBuilding?.type) return selectedBuilding.type;
        if (currentProperty?.type) return currentProperty.type;
        return '';
    }, [selectedBuilding, currentProperty]);

    const propertyOptions = useMemo(
        () => propertyList.map((p) => ({ value: p._id, label: `${p.title || p.name || 'Unnamed'}${p.location ? `, ${p.location}` : ''}` })),
        [propertyList]
    );

    const buildingOptions = useMemo(
        () => buildings.map((b) => ({ value: b.buildingName, label: b.buildingName })),
        [buildings]
    );

    const selectedPropertyOption = useMemo(
        () => propertyOptions.find((o) => o.value === selectedFilters.property_id) || null,
        [propertyOptions, selectedFilters.property_id]
    );

    const selectedBuildingOption = useMemo(
        () => buildingOptions.find((o) => o.value === selectedFilters.building_name) || null,
        [buildingOptions, selectedFilters.building_name]
    );

    const selectedFrequencyOption = useMemo(
        () => frequencyOptions.find((o) => o.value === selectedFilters.frequencyId) || null,
        [frequencyOptions, selectedFilters.frequencyId]
    );

    useEffect(() => {
        if (buildings.length === 1 && !selectedFilters.building_name && selectedFilters.property_id) {
            onPropertySelectionChange({
                ...selectedFilters,
                building_name: buildings[0].buildingName,
                property_type: buildings[0].buildingType || buildings[0].type || currentProperty?.type || '',
            });
        }
    }, [buildings, selectedFilters.property_id, selectedFilters.building_name, onPropertySelectionChange, currentProperty]);

    useEffect(() => {
        if (!selectedFilters.property_id) return;
        if (derivedPropertyType && derivedPropertyType !== selectedFilters.property_type) {
            onPropertySelectionChange({ ...selectedFilters, property_type: derivedPropertyType });
        }
    }, [derivedPropertyType, selectedFilters, onPropertySelectionChange]);

    const handlePropertyChange = useCallback(
        (selected) => {
            const newId = selected?.value || '';
            const prop = propertyList.find((p) => p._id === newId);
            const propBuildings = prop?.building?.filter((b) => b?.buildingName) ?? [];
            const autoBuilding = propBuildings.length === 1 ? propBuildings[0].buildingName : '';
            const autoType = propBuildings.length === 1
                ? propBuildings[0].buildingType || propBuildings[0].type || prop?.type || ''
                : prop?.type || '';
            onPropertySelectionChange({ property_id: newId, building_name: autoBuilding, property_type: autoType, year: selectedFilters.year, overdueOnly: false, frequencyId: '' });
        },
        [propertyList, onPropertySelectionChange, selectedFilters.year]
    );

    const handleBuildingChange = useCallback(
        (selected) => {
            const buildingName = selected?.value || '';
            const building = buildings.find((b) => b.buildingName === buildingName);
            const buildingType = building?.buildingType || building?.type || currentProperty?.type || '';
            onPropertySelectionChange({ ...selectedFilters, building_name: buildingName, property_type: buildingType });
        },
        [buildings, currentProperty, selectedFilters, onPropertySelectionChange]
    );

    const handleFilterChange = useCallback(
        (key) => (value) => onPropertySelectionChange({ ...selectedFilters, [key]: value }),
        [selectedFilters, onPropertySelectionChange]
    );

    const selectStyles = {
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        menu: (base) => ({ ...base, zIndex: 9999 }),
        control: (base, { isDisabled }) => ({
            ...base,
            zIndex: 2,
            backgroundColor: isDisabled ? '#f8f9fa' : base.backgroundColor,
            cursor: isDisabled ? 'not-allowed' : 'default',
        }),
    };

    return (
        <Form className="mb-4">
            <Row className="g-3 align-items-end">
                <Col md={3}>
                    <FormGroup>
                        <Label className="fw-semibold">Property</Label>
                        <Select
                            value={selectedPropertyOption}
                            onChange={handlePropertyChange}
                            options={propertyOptions}
                            placeholder="Select Property"
                            isClearable
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </FormGroup>
                </Col>

                {hasBuildings && (
                    <Col md={3}>
                        <FormGroup>
                            <Label className="fw-semibold">Building {hasMultipleBuildings ? '*' : '(auto)'}</Label>
                            <Select
                                value={selectedBuildingOption}
                                onChange={handleBuildingChange}
                                options={buildingOptions}
                                placeholder="Select Building"
                                isClearable={hasMultipleBuildings}
                                isDisabled={!hasMultipleBuildings}
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                        </FormGroup>
                    </Col>
                )}

                <Col md={2}>
                    <FormGroup>
                        <Label className="fw-semibold" for="it-year-select">Year</Label>
                        <Input
                            id="it-year-select"
                            type="select"
                            value={selectedFilters.year}
                            onChange={(e) => handleFilterChange('year')(Number(e.target.value))}
                        >
                            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                        </Input>
                    </FormGroup>
                </Col>

                {derivedPropertyType && (
                    <Col md={2}>
                        <FormGroup>
                            <Label className="fw-semibold">Type</Label>
                            <div className="form-control" style={{ backgroundColor: '#f8f9fa', color: '#495057', cursor: 'default', fontWeight: 500 }}>
                                {derivedPropertyType}
                            </div>
                        </FormGroup>
                    </Col>
                )}

                <Col className="ms-auto" style={{ flexGrow: 0 }}>
                    <div className="d-flex align-items-center gap-2 justify-content-end">
                        <Button color="success" onClick={onExportExcel} disabled={!selectedFilters.property_id} style={{ minWidth: '140px' }}>
                            <FeatherIcon icon="download" size={18} className="me-2" />
                            Export Excel
                        </Button>
                        <Button color="primary" outline onClick={() => setIsFiltersOpen((prev) => !prev)} style={{ minWidth: '120px' }}>
                            <FeatherIcon icon="filter" size={18} className="me-2" />
                            Filters
                        </Button>
                    </div>
                </Col>
            </Row>

            <Collapse isOpen={isFiltersOpen}>
                <Row className="g-3 mt-3">
                    <Col md={3}>
                        <FormGroup>
                            <Label className="fw-semibold">Frequency</Label>
                            <Select
                                value={selectedFrequencyOption}
                                onChange={(selected) => handleFilterChange('frequencyId')(selected?.value || '')}
                                options={frequencyOptions}
                                placeholder="All frequencies"
                                isClearable
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                        </FormGroup>
                    </Col>
                    <Col md={3}>
                        <FormGroup>
                            <Label className="fw-semibold">Overdue Only</Label>
                            <div className="mt-2">
                                <Input
                                    type="checkbox"
                                    checked={selectedFilters.overdueOnly || false}
                                    onChange={(e) => handleFilterChange('overdueOnly')(e.target.checked)}
                                />{' '}
                                <Label check>Show only overdue inspections</Label>
                            </div>
                        </FormGroup>
                    </Col>
                </Row>
            </Collapse>
        </Form>
    );
};

export { PropertySelector };